import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import "./CartPage.css";
import { connectRealtimeSocket } from "../ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
  "fullName",
  "name",
  "userName",
  "username",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: { source: "CartPage", action: "clear-auth" },
    })
  );
};

const getStoredUser = () => {
  const rawUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!rawUser) return {};

  try {
    const parsed = JSON.parse(rawUser);

    if (!parsed || typeof parsed !== "object") return {};

    return parsed;
  } catch {
    return {};
  }
};

/* ==============================
   LOCAL CART HELPERS
============================== */
const saveCartToLocal = (cartItems = []) => {
  try {
    localStorage.setItem(
      "cart",
      JSON.stringify(Array.isArray(cartItems) ? cartItems : [])
    );
  } catch {
    // localStorage can fail in private mode
  }
};

const notifyCartUpdated = (cartItems = [], action = "cart-updated") => {
  saveCartToLocal(cartItems);

  window.dispatchEvent(
    new CustomEvent("cartUpdated", {
      detail: {
        source: "CartPage",
        action,
        count: Array.isArray(cartItems) ? cartItems.length : 0,
      },
    })
  );
};

/* ==============================
   FORMAT HELPERS
============================== */
const normalizeDomain = (value = "") => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "")
    .replace(/\s+/g, "");
};

const getDomainExtension = (domainName = "") => {
  const clean = normalizeDomain(domainName);
  const parts = clean.split(".");

  if (parts.length < 2) return "";

  return `.${parts[parts.length - 1]}`;
};

const parsePriceToNumber = (value) => {
  if (typeof value === "number") return value;

  const clean = String(value || "")
    .replace(/[₹,\s]/g, "")
    .replace(/[^0-9.]/g, "");

  const amount = Number(clean);

  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const getItemPrice = (item = {}) => {
  const rawPrice = Number(item.rawPrice || item.raw_price || 0);

  if (rawPrice > 0) return rawPrice;

  return parsePriceToNumber(item.price);
};

const formatInr = (amount = 0) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
};

/* ==============================
   RAZORPAY SCRIPT LOADER
============================== */
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true), {
        once: true,
      });
      existingScript.addEventListener("error", () => resolve(false), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

const CartPage = () => {
  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [removingDomain, setRemovingDomain] = useState("");
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  const mountedRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  const lastFetchAtRef = useRef(0);
  const refreshTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const subtotal = useMemo(() => {
    return cartItems.reduce((total, item) => {
      const price = getItemPrice(item);
      const quantity = Number(item.quantity || 1);

      return total + price * quantity;
    }, 0);
  }, [cartItems]);

  const totalItems = useMemo(() => cartItems.length, [cartItems]);

  /* ==============================
     PROTECTED FETCH
  ============================== */
  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401 || response.status === 403) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    if (response.status === 429) {
      throw new Error("Too many requests. Please wait a moment and try again.");
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  /* ==============================
     LOAD BACKEND CART
     IMPORTANT:
     - No cartUpdated dispatch here
     - Prevents API/event loop
  ============================== */
  const fetchCart = useCallback(
    async ({ silent = false, force = false } = {}) => {
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchAtRef.current;

      if (!force && timeSinceLastFetch < 1200) {
        return;
      }

      if (fetchInProgressRef.current) {
        return;
      }

      fetchInProgressRef.current = true;
      lastFetchAtRef.current = now;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        if (!silent && mountedRef.current) {
          setLoading(true);
        }

        if (mountedRef.current) {
          setStatus({ type: "", message: "" });
        }

        const data = await authFetch("/domains/cart", {
          signal: abortControllerRef.current.signal,
        });

        const safeCart = Array.isArray(data?.data) ? data.data : [];

        saveCartToLocal(safeCart);

        if (mountedRef.current) {
          setCartItems(safeCart);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;

        if (mountedRef.current) {
          setCartItems([]);

          setStatus({
            type: "error",
            message: error.message || "Failed to load cart.",
          });
        }

        if (
          String(error.message || "")
            .toLowerCase()
            .includes("session expired")
        ) {
          setTimeout(() => {
            navigate("/signin", { replace: true });
          }, 700);
        }
      } finally {
        fetchInProgressRef.current = false;

        if (!silent && mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [authFetch, navigate]
  );

  const scheduleCartRefresh = useCallback(
    ({ force = false, delay = 500 } = {}) => {
      clearTimeout(refreshTimerRef.current);

      refreshTimerRef.current = setTimeout(() => {
        fetchCart({ silent: true, force });
      }, delay);
    },
    [fetchCart]
  );

  useEffect(() => {
    mountedRef.current = true;

    fetchCart({ silent: false, force: true });

    return () => {
      mountedRef.current = false;

      clearTimeout(refreshTimerRef.current);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchCart]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const token = getToken();

    if (!token) {
      setRealtimeStatus("offline");
      return undefined;
    }

    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    const softRefresh = () => {
      scheduleCartRefresh({ force: false, delay: 800 });
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleDomainStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.message) {
        setStatus({
          type: "success",
          message: payload.message,
        });
      }

      softRefresh();
    };

    const handleAvailabilityUpdate = (payload = {}) => {
      setRealtimeStatus("connected");

      const soldDomain = normalizeDomain(payload?.domain);

      if (!soldDomain) return;

      setCartItems((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];

        const hasDomain = safePrev.some(
          (item) => normalizeDomain(item.name || item.domainName) === soldDomain
        );

        const filtered = safePrev.filter(
          (item) => normalizeDomain(item.name || item.domainName) !== soldDomain
        );

        if (hasDomain) {
          setStatus({
            type: "error",
            message: `${soldDomain} is now taken and removed from your cart.`,
          });

          notifyCartUpdated(filtered, "domain-sold-removed");
        }

        return filtered;
      });

      softRefresh();
    };

    const handlePaymentStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.status === "success") {
        setStatus({
          type: "success",
          message: payload.message || "Payment completed successfully.",
        });
      }

      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      clearAuth();
      navigate("/signin", { replace: true });
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("domain:status", handleDomainStatus);
    socket.on("domain:availability:update", handleAvailabilityUpdate);
    socket.on("payment:status", handlePaymentStatus);
    socket.on("dashboard:update", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("domain:status", handleDomainStatus);
      socket.off("domain:availability:update", handleAvailabilityUpdate);
      socket.off("payment:status", handlePaymentStatus);
      socket.off("dashboard:update", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);
    };
  }, [navigate, scheduleCartRefresh]);

  /* ==============================
     ACTIONS
  ============================== */
  const handleBack = () => {
    navigate(-1);
  };

  const handleRemoveItem = async (item) => {
    const domainName = normalizeDomain(item?.name || item?.domainName);

    if (!domainName) return;

    try {
      setRemovingDomain(domainName);
      setStatus({ type: "", message: "" });

      const data = await authFetch(
        `/domains/cart/${encodeURIComponent(domainName)}`,
        {
          method: "DELETE",
        }
      );

      const safeCart = Array.isArray(data?.data) ? data.data : [];

      setCartItems(safeCart);
      notifyCartUpdated(safeCart, "remove-item");

      setStatus({
        type: "success",
        message: data?.message || `${domainName} removed from cart.`,
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Unable to remove item.",
      });
    } finally {
      setRemovingDomain("");
    }
  };

  const handleClearCart = async () => {
    if (!cartItems.length) return;

    try {
      setStatus({ type: "", message: "" });

      const domains = cartItems
        .map((item) => normalizeDomain(item?.name || item?.domainName))
        .filter(Boolean);

      for (const domainName of domains) {
        await authFetch(`/domains/cart/${encodeURIComponent(domainName)}`, {
          method: "DELETE",
        });
      }

      setCartItems([]);
      notifyCartUpdated([], "clear-cart");

      setStatus({
        type: "success",
        message: "Cart cleared successfully.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Failed to clear cart.",
      });

      scheduleCartRefresh({ force: true, delay: 500 });
    }
  };

  /* ==============================
     CHECKOUT FLOW
  ============================== */
  const handleCheckout = async () => {
    try {
      setCheckoutLoading(true);
      setStatus({ type: "", message: "" });

      const token = getToken();

      if (!token) {
        clearAuth();
        navigate("/signin", { replace: true });
        return;
      }

      if (!cartItems.length) {
        setStatus({
          type: "error",
          message: "Your cart is empty.",
        });
        return;
      }

      if (!subtotal || subtotal <= 0) {
        setStatus({
          type: "error",
          message: "Invalid cart amount.",
        });
        return;
      }

      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        throw new Error("Payment gateway failed to load. Please try again.");
      }

      const domainNames = cartItems
        .map((item) => normalizeDomain(item.name || item.domainName))
        .filter(Boolean);

      if (!domainNames.length) {
        throw new Error("No valid domains found in cart.");
      }

      const productName =
        domainNames.length === 1
          ? domainNames[0]
          : `Domain Purchase - ${domainNames.length} domains`;

      const productPayload = {
        productType: "domain",
        productName,
        planName: productName,
        domainName: domainNames.join(","),
        extension: domainNames.map(getDomainExtension).join(","),
        amount: subtotal,
      };

      const orderData = await authFetch("/payments/create-order", {
        method: "POST",
        body: JSON.stringify(productPayload),
      });

      if (!orderData?.order?.id || !orderData?.key) {
        throw new Error("Invalid payment order response.");
      }

      const storedUser = getStoredUser();

      const razorpayOptions = {
        key: orderData.key,
        amount: orderData.order.amount,
        currency: orderData.order.currency || "INR",
        name: "DoHostingPay",
        description: productName,
        order_id: orderData.order.id,

        prefill: {
          name: storedUser?.fullName || storedUser?.name || "",
          email: storedUser?.email || "",
          contact: storedUser?.phone || "",
        },

        notes: {
          productType: "domain",
          totalDomains: String(domainNames.length),
        },

        theme: {
          color: "#6d28d9",
        },

        modal: {
          confirm_close: true,
          ondismiss: () => {
            setCheckoutLoading(false);
            setStatus({
              type: "error",
              message:
                "Payment popup closed. Domain purchase was not completed.",
            });
          },
        },

        handler: async (response) => {
          try {
            setStatus({
              type: "success",
              message: "Payment received. Verifying securely...",
            });

            const verifiedPayment = await authFetch("/payments/verify", {
              method: "POST",
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                ...productPayload,
              }),
            });

            const paymentId =
              verifiedPayment?.payment?.paymentId ||
              verifiedPayment?.payment?.payment_id ||
              verifiedPayment?.paymentId ||
              response.razorpay_payment_id;

            const orderId =
              verifiedPayment?.payment?.orderId ||
              verifiedPayment?.payment?.order_id ||
              verifiedPayment?.orderId ||
              response.razorpay_order_id;

            if (!paymentId) {
              throw new Error("Verified payment id missing.");
            }

            await authFetch("/domains/mock-purchase", {
              method: "POST",
              body: JSON.stringify({
                paymentId,
                orderId,
                paymentMode: verifiedPayment?.payment?.mode || "test",
                amount: subtotal,
                purchasedDomains: domainNames,
              }),
            });

            setCartItems([]);
            notifyCartUpdated([], "checkout-success");

            setStatus({
              type: "success",
              message: "Domain payment successful. Your domains are now active.",
            });

            setTimeout(() => {
              navigate("/domains");
            }, 900);
          } catch (error) {
            setStatus({
              type: "error",
              message:
                error.message ||
                "Payment verified but domain activation failed.",
            });
          } finally {
            setCheckoutLoading(false);
          }
        },
      };

      const razorpay = new window.Razorpay(razorpayOptions);

      razorpay.on("payment.failed", () => {
        setCheckoutLoading(false);
        setStatus({
          type: "error",
          message: "Payment failed. Please try again.",
        });
      });

      razorpay.open();
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Unable to start checkout.",
      });
      setCheckoutLoading(false);
    }
  };

  return (
    <section className="cart-page">
      <div className="cart-container">
        <div className="cart-topbar">
          <button type="button" className="cart-back-btn" onClick={handleBack}>
            ← Back
          </button>

          <div className="cart-realtime-chip">
            <span
              className={
                realtimeStatus === "connected"
                  ? "cart-realtime-dot online"
                  : "cart-realtime-dot offline"
              }
            />

            <span>
              {realtimeStatus === "connected"
                ? "Realtime connected"
                : realtimeStatus === "connecting"
                ? "Connecting realtime..."
                : "Realtime offline"}
            </span>
          </div>
        </div>

        <div className="cart-header">
          <div>
            <span className="cart-badge-title">Shopping Cart</span>
            <h1>Your Cart</h1>
            <p>Manage selected domains or products from here.</p>
          </div>

          {cartItems.length > 0 && (
            <button
              type="button"
              className="cart-clear-btn"
              onClick={handleClearCart}
              disabled={checkoutLoading || removingDomain}
            >
              Clear Cart
            </button>
          )}
        </div>

        {status.message ? (
          <div className={`cart-alert ${status.type}`}>{status.message}</div>
        ) : null}

        <div className="cart-layout">
          <div className="cart-left">
            {loading ? (
              <div className="cart-empty-card">
                <h3>Loading cart...</h3>
                <p>Please wait while we fetch your selected items.</p>
              </div>
            ) : cartItems.length === 0 ? (
              <div className="cart-empty-card">
                <h3>Your cart is empty</h3>
                <p>Add domains to view them here.</p>
              </div>
            ) : (
              cartItems.map((item, index) => {
                const domainName = normalizeDomain(item.name || item.domainName);
                const itemPrice = getItemPrice(item);
                const quantity = Number(item.quantity || 1);
                const isRemoving = removingDomain === domainName;

                return (
                  <div
                    className="cart-item-card"
                    key={item.id || domainName || index}
                  >
                    <div className="cart-item-info">
                      <h3>{domainName || item.name || "Domain Name"}</h3>

                      <p>
                        {item.extension ||
                          getDomainExtension(domainName) ||
                          "Selected domain"}
                      </p>

                      <span className="cart-item-meta">Qty: {quantity}</span>
                    </div>

                    <div className="cart-item-right">
                      <strong>{formatInr(itemPrice * quantity)}</strong>

                      <button
                        type="button"
                        className="cart-remove-btn"
                        onClick={() => handleRemoveItem(item)}
                        disabled={isRemoving || checkoutLoading}
                      >
                        {isRemoving ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="cart-summary-card">
            <h3>Order Summary</h3>

            <div className="cart-summary-row">
              <span>Total Items</span>
              <strong>{totalItems}</strong>
            </div>

            <div className="cart-summary-row">
              <span>Subtotal</span>
              <strong>{formatInr(subtotal)}</strong>
            </div>

            <div className="cart-summary-row total">
              <span>Total</span>
              <strong>{formatInr(subtotal)}</strong>
            </div>

            <button
              type="button"
              className="cart-checkout-btn"
              onClick={handleCheckout}
              disabled={checkoutLoading || loading || cartItems.length === 0}
            >
              {checkoutLoading ? "Processing..." : "Proceed to Checkout"}
            </button>

            <p className="cart-secure-note">
              Secure Razorpay test payment. Domain activates only after backend
              verification.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CartPage;